import { useCallback, useMemo } from 'react'
import type { ResponsiveContainerProps } from 'recharts'
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts'
import { AxisDomain } from 'recharts/types/util/types'
import {
    NyuDataPointsWithParsedTime,
    NyuDataProperties,
    NyuGroupProperties,
    NyuSeriesProperties,
} from '../../../shared/types'
import useBreakpoint from '../hooks/useBreakpoint'
import { useHighContrastDisplay } from '../hooks/useHighContrastMode'
import { formatDataPointValue, toBaseDataKey } from '../utils/helpers'
import { lineTypes } from '../utils/options'
import {
    defaultReferenceLineColor,
    useReferenceLines,
} from '../utils/referenceLines'
import { AxisLabelAboveChart } from './AxisLabelAboveChart'
import CustomActiveDot from './Graph/CustomActiveDot'
import CustomLabel from './Graph/CustomLabel'
import CustomLegend from './Graph/CustomLegend'
import CustomTooltip from './Graph/CustomTooltip'
import { useCustomDot } from './Graph/HighContrastMode/CustomDot'
import {
    RECHARTS_LEGEND_TYPES,
    SHAPES,
} from './Graph/HighContrastMode/constants'
import { calculateShapeVisibility } from './Graph/HighContrastMode/shapeUtils'

const minYearSpanForFixedTicks = 4

export function CustomLineChart({
    dataPoints: unsortedDataPoints,
    dataProperties,
    seriesProperties,
    groups,
    chartWidth,
    chartHeight,
    onResize,
}: {
    dataPoints: NyuDataPointsWithParsedTime
    dataProperties: NyuDataProperties
    seriesProperties: NyuSeriesProperties
    groups: NyuGroupProperties[]
    chartWidth: number
    chartHeight: number
    onResize?: ResponsiveContainerProps['onResize']
}) {
    const dataPoints = useMemo(() => {
        const sortedDataPoints = unsortedDataPoints.toSorted(
            ({ time: a }, { time: b }) => a - b
        )
        return sortedDataPoints
    }, [unsortedDataPoints])

    const highContrastDisplay = useHighContrastDisplay(
        dataProperties,
        seriesProperties
    )

    // Check if high contrast mode should be active for this chart type
    const isHighContrastActive = highContrastDisplay.showShapes

    // Pre-calculate which data points should show shapes for each series
    const shapeVisibilityMap = useMemo(() => {
        return calculateShapeVisibility(
            seriesProperties,
            dataPoints,
            isHighContrastActive
        )
    }, [seriesProperties, dataPoints, isHighContrastActive])

    // Use custom dot hook
    const { createDotComponent } = useCustomDot({
        isHighContrastActive,
        seriesProperties,
        shapeVisibilityMap,
    })

    const renderTooltip = useCallback(
        (props: TooltipProps<number, string>) => (
            <CustomTooltip
                {...props}
                seriesProperties={seriesProperties}
                dataProperties={dataProperties}
            />
        ),
        [seriesProperties, dataProperties]
    )

    const hasMultipleAxes = !!dataProperties.secondaryAxis
    const rightMargin = hasMultipleAxes ? 40 : 20

    const breakpointSmOrLarger = useBreakpoint('sm')
    const breakpointMdOrLarger = useBreakpoint('md')
    const axisLabelOnChart = breakpointSmOrLarger

    const leftLabel = (
        <CustomLabel
            chartWidth={chartWidth}
            chartHeight={chartHeight}
            side="left"
        >
            {dataProperties.axisTitle}
        </CustomLabel>
    )

    const rightLabel = (
        <CustomLabel
            chartWidth={chartWidth}
            chartHeight={chartHeight}
            side="right"
        >
            {dataProperties.secondaryAxis?.axisTitle}
        </CustomLabel>
    )

    const xAxisDomain = ['auto', 'auto']
    const yAxisDomain = (dataProperties.axisDomain as AxisDomain) || [0, 'auto']

    const times = dataPoints.map((dataPoint) => dataPoint.time)
    const minYear = Math.ceil(Math.min(...times))
    const maxYear = Math.floor(Math.max(...times))
    const yearSpan = maxYear - minYear
    const yearTicks =
        yearSpan <= minYearSpanForFixedTicks
            ? undefined
            : Array.from(
                  { length: maxYear - minYear + 1 },
                  (_, i) => minYear + i
              )

    const formatYearWithPadding = useCallback(
        (time: number) => {
            const dataPoint = dataPoints.find(
                (dataPoint) => dataPoint.time === time
            )
            if (yearTicks || !dataPoint) {
                return ` '${time.toString().slice(-2)} `
            }
            return ` ${dataPoint.axisLabelTimeValue} `
        },
        [dataPoints, yearTicks]
    )

    const lines = groups.flatMap(({ lines }) => lines)

    const referenceLines = useReferenceLines(
        dataProperties,
        seriesProperties,
        dataPoints
    )

    return (
        <>
            {!axisLabelOnChart && (
                <AxisLabelAboveChart dataProperties={dataProperties} />
            )}
            <ResponsiveContainer
                height={chartHeight}
                onResize={onResize}
                className="focus-visible:[&_svg]:!shadow-none" // See 'CSS Bleeding' in README.md
            >
                <LineChart
                    accessibilityLayer={true}
                    data={dataPoints}
                    margin={{
                        left: axisLabelOnChart ? 35 : 0,
                        right: axisLabelOnChart ? rightMargin : 0,
                    }}
                >
                    <CartesianGrid vertical={false} yAxisId="primaryAxis" />
                    <XAxis
                        dataKey="time"
                        type="number"
                        scale="linear"
                        domain={xAxisDomain}
                        allowDecimals={true}
                        ticks={yearTicks}
                        tickFormatter={formatYearWithPadding}
                        height={25}
                    />
                    <YAxis
                        yAxisId="primaryAxis"
                        orientation="left"
                        label={axisLabelOnChart ? leftLabel : undefined}
                        domain={yAxisDomain}
                        tickFormatter={(value) =>
                            formatDataPointValue(value, dataProperties.format)
                        }
                    />
                    {hasMultipleAxes && (
                        <YAxis
                            yAxisId="secondaryAxis"
                            orientation="right"
                            label={axisLabelOnChart ? rightLabel : undefined}
                            domain={yAxisDomain}
                            tickFormatter={(value) =>
                                formatDataPointValue(
                                    value,
                                    dataProperties.secondaryAxis!.format
                                )
                            }
                        />
                    )}
                    {breakpointMdOrLarger && (
                        <Tooltip content={renderTooltip} />
                    )}
                    {lines.map((line, lineIndex) => {
                        if (line.secondaryAxis && !hasMultipleAxes) {
                            console.error(
                                `Error: secondaryAxis is not set in dataProperties but referenced in seriesProperties (key=${line.key})`
                            )
                        }

                        const enableSecondaryAxis =
                            line.secondaryAxis && hasMultipleAxes

                        const yAxisId = enableSecondaryAxis
                            ? 'secondaryAxis'
                            : 'primaryAxis'

                        const unit = enableSecondaryAxis
                            ? dataProperties.secondaryAxis!.format
                            : dataProperties.format

                        // Calculate the correct shape index for forecast and highlight lines
                        // First, find the index in seriesProperties for this line
                        const seriesIndex = seriesProperties.findIndex(
                            (series) => series.key === line.key
                        )
                        let shapeIndex =
                            seriesIndex >= 0 ? seriesIndex : lineIndex

                        const baseKey = toBaseDataKey(line.key)
                        if (baseKey !== line.key) {
                            const baseSeriesIndex = seriesProperties.findIndex(
                                (series) => series.key === baseKey
                            )
                            if (baseSeriesIndex >= 0) {
                                shapeIndex = baseSeriesIndex
                            }
                        }

                        return (
                            <Line
                                key={line.key}
                                dot={
                                    isHighContrastActive
                                        ? createDotComponent(shapeIndex)
                                        : false
                                }
                                legendType={
                                    isHighContrastActive
                                        ? RECHARTS_LEGEND_TYPES[
                                              SHAPES[shapeIndex % SHAPES.length]
                                          ]
                                        : 'line'
                                }
                                yAxisId={yAxisId}
                                unit={unit}
                                dataKey={line.key}
                                stroke={line.color?.toLowerCase()}
                                activeDot={CustomActiveDot}
                                isAnimationActive={false}
                                strokeWidth={line.strokewidth || 2}
                                strokeDasharray={
                                    lineTypes[line.linetype || 'solid']
                                }
                            />
                        )
                    })}
                    {referenceLines.map((referenceLine) => (
                        <ReferenceLine
                            key={referenceLine.id}
                            y={referenceLine.value}
                            yAxisId={
                                referenceLine.secondaryAxis && hasMultipleAxes
                                    ? 'secondaryAxis'
                                    : 'primaryAxis'
                            }
                            stroke={
                                referenceLine.color || defaultReferenceLineColor
                            }
                            strokeWidth={referenceLine.strokewidth || 2}
                            strokeDasharray={
                                lineTypes[referenceLine.linetype || 'solid']
                            }
                            ifOverflow="extendDomain"
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
            <CustomLegend
                seriesProperties={seriesProperties}
                dataProperties={dataProperties}
                referenceLines={referenceLines}
            />
        </>
    )
}
